import React, { useState } from "react"
import { connect, useSelector } from "react-redux"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import type { State } from "@/signomatic/store/reducers"
import Toolbox from "@/signomatic/components/toolbox"
import Icons from "@/images/signomatic/materials"
import AutoGrid from "@/signomatic/components/auto_grid"
import { Trans } from "react-i18next"
import type { ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { optionForcedState } from "@/signomatic/app/sign_validator"
import { matchArray } from "@/signomatic/util"
import SelectedIndicator from "@/signomatic/components/selected_indicator"

const reason = (type: string | null) => {
  switch (type) {
    case "color":
      return "toolbox.material_disabled_color"
    case "border_color":
      return "toolbox.material_disabled_border_color"
    case "content_color":
      return "toolbox.material_disabled_content_color"
    case "size":
      return "toolbox.material_disabled_size"
    default:
      return ""
  }
}

interface MaterialProps {
  name: string
  imageName: string
  enabled: boolean
  described: boolean
  descriptionKey?: string
  active: boolean
  update: () => void
}

const Material = ({ name, imageName, enabled, described, descriptionKey, active, update }: MaterialProps) => {
  const { t } = useTranslation()

  return described ? (
    <div className={cn("described", "selection", `material-${name}`, { disabled: !enabled, active })} onClick={update}>
      <div className="contents">
        <div className="image">{Icons[imageName] ? React.createElement(Icons[imageName]) : ""}</div>
        <div className="details">
          <h5>{t(`material_type.${name}`)}</h5>
          <p className="paragraph-2xs">
            <Trans i18nKey={descriptionKey} />
          </p>
        </div>
      </div>
      <div className="info-row right">
        <SelectedIndicator active={active} />
      </div>
    </div>
  ) : (
    <div className={cn("item", "selection", `material-${name}`, { disabled: !enabled, active })} onClick={update}>
      <div className="image">{Icons[imageName] ? React.createElement(Icons[imageName]) : ""}</div>
      <div className="name paragraph-2xs center mb-0">{t(`material_type.${name}`)}</div>
    </div>
  )
}

interface MaterialToolboxState {
  signType: string
  currentSignData: SignData | null
  signState: SignState | null
}

const MaterialToolbox: React.FC<ToolboxStripItemProps> = ({ sign }) => {
  const { t } = useTranslation()
  const [disabledMessage, setDisabledMessage] = useState<string>()
  const { signState, currentSignData, signType } = useSelector<State, MaterialToolboxState>(
    ({ signState, currentSignData, signType }) => ({
      signState,
      currentSignData,
      signType,
    })
  )
  const described = signType === "rollup"

  if (!currentSignData || !signState) {
    return null
  }

  const items = currentSignData.materials.map(material => {
    const active = signState.material === material.name
    let enabled = material.enabled
    let forcedState: Partial<SignState> = {}

    if (material.filters) {
      if (material.filters.hidden || (material.filters.legacy && !active)) {
        return null
      }
    }

    if (!enabled && matchArray(material.disabled_reason, ["size"])) {
      enabled = true

      forcedState = optionForcedState(material, signState, ["size"])
    }

    const imageName = described && material.name === "regular" ? "rollup_regular" : material.name
    const descriptionKey = described ? `info.rollup_${material.name}` : undefined

    return (
      <Material
        key={material.name}
        name={material.name}
        imageName={imageName}
        descriptionKey={descriptionKey}
        active={active}
        enabled={enabled || false}
        described={described}
        update={() => {
          if (enabled) {
            sign.updateState({ material: material.name, ...forcedState })
            setDisabledMessage(undefined)
          } else if (material.disabled_reason) {
            setDisabledMessage(material.disabled_reason[0])
          }
        }}
      />
    )
  })

  return (
    <Toolbox className="toolbox-material">
      {described ? (
        <div className="material-toolbox">{items}</div>
      ) : (
        <div className="inline-toolbox">
          <AutoGrid>{items}</AutoGrid>
          {disabledMessage && (
            <div className="disabled-reason paragraph-2xs bold mb-0">{t(reason(disabledMessage))}</div>
          )}
        </div>
      )}
    </Toolbox>
  )
}

export default connect(({ signType, signState, currentSignData }: State) => ({
  signType,
  signState,
  data: currentSignData,
}))(MaterialToolbox)
